import { apiDownload, apiRequest } from './client.js';

/**
 * Org-level reads. Currently a single endpoint that surfaces the
 * dynamic FX-currency list + missing-rate gaps used by the FX admin
 * page.
 */

type Currency = 'NGN' | 'GHS' | 'CDF' | 'USD';
type Country = 'NG' | 'GH' | 'CD';

export interface MissingPairReason {
  kind: 'rollup' | 'spec_conversion';
  message: string;
}

export interface RequiredFxPair {
  /** Canonical sorted pair: currencyA <= currencyB alphabetically. */
  currencyA: Currency;
  currencyB: Currency;
  reasons: MissingPairReason[];
}

export interface FxCurrenciesResponse {
  baseCurrency: Currency;
  /** Country codes of every active employee — drives the currency list. */
  employeeCountries: Country[];
  /** Allowed currencies: {baseCurrency, USD, ...employee-country currencies}. */
  currencies: Currency[];
  /**
   * Pairs the org NEEDS but hasn't set yet. Driven by each active
   * employee's country (roll-up) + their current compensation
   * currency (spec conversion). Empty array = fully covered.
   */
  missingPairs: RequiredFxPair[];
}

export function getFxCurrencies(): Promise<FxCurrenciesResponse> {
  return apiRequest<FxCurrenciesResponse>('/v1/orgs/fx-currencies');
}

// ---------------------------------------------------------------------------
// ADR 0010 Phase 4 — International Remote Workers Roster
// ---------------------------------------------------------------------------

export interface RosterEmployee {
  employeeNumber: string;
  firstName: string;
  lastName: string;
  country: Country;
  payrollModel: 'international_remote';
  hireDate: string;
  employmentType: string | null;
  createdAt: string;
}

export interface InternationalRemoteRosterResponse {
  organizationId: string;
  organizationName: string;
  primaryCountry: Country;
  employees: RosterEmployee[];
  totalCount: number;
}

/** GET /v1/orgs/international-remote-roster (JSON). owner/admin/hr. */
export function getInternationalRemoteRoster(): Promise<InternationalRemoteRosterResponse> {
  return apiRequest<InternationalRemoteRosterResponse>('/v1/orgs/international-remote-roster');
}

/** Same endpoint as a CSV download (?format=csv). */
export function downloadInternationalRemoteRoster(orgName?: string): Promise<void> {
  const date = new Date().toISOString().slice(0, 10);
  const slug = (orgName ?? 'roster').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return apiDownload(
    '/v1/orgs/international-remote-roster?format=csv',
    `international-remote-roster-${slug}-${date}.csv`,
  );
}
